import React, { Fragment, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

function classNames(...classes: string[]) {
	return classes.filter(Boolean).join(' ');
}

type Option = { value: string; label: string; avatar?: string };
interface SelectFieldProps {
	options: Option[];
	label?: string;
	color?: string;
	className?: string;
	onChange?: ({ value, label }: { value: string; label: string }) => void;
}
export function SelectField({ options, label, onChange, color }: SelectFieldProps) {
	const [selected, setSelected] = useState<Option>();

	const handleChange = (data: Option) => {
		setSelected(data);
		onChange && onChange(data);
	};
	return (
		<Listbox value={selected} onChange={handleChange}>
			{({ open }) => (
				<>
					<Listbox.Label className="block text-lg font-medium leading-6 text-gray-900">{label}</Listbox.Label>
					<div className="relative mt-3">
						<Listbox.Button
							className={`relative w-full cursor-default rounded-lg bg-gray-50 mb-4 border border-gray-300 ${
								selected ? 'text-gray-900' : 'text-gray-400'
							} ${
								color ? `focus:ring-[${color}]` : 'focus:ring-blue-500 focus:border-blue-500  focus:ring-indigo-500 '
							} text-sm px-2.5 py-2.5  text-left shadow-sm  focus:outline-none focus:ring-2  `}
						>
							{selected ? (
								<>
									<span className="flex items-center">
										{selected.avatar && (
											<div className="h-5 w-5 flex-shrink-0 rounded-full">
												<p>{selected.avatar}</p>
											</div>
										)}

										<p className="ml-2 block truncate ">{selected.label}</p>
									</span>
									<span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
										<ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
									</span>
								</>
							) : (
								<>
									<p className="truncate">Select option</p>

									<span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
										<ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
									</span>
								</>
							)}
						</Listbox.Button>

						<Transition
							show={open}
							as={Fragment}
							leave="transition ease-in duration-100"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Listbox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
								{options.map((option) => (
									<Listbox.Option
										key={option.value}
										className={({ active }) =>
											classNames(
												active ? `bg-[${color}] text-white` : 'text-gray-900',
												'relative cursor-default select-none py-2 pl-3 pr-9'
											)
										}
										value={option}
									>
										{({ selected, active }) => (
											<>
												<div className="flex items-center">
													{option.avatar && (
														<div className="h-5 w-5 flex-shrink-0 rounded-full">
															<p>{option.avatar}</p>
														</div>
													)}

													<span
														className={classNames(selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate')}
													>
														{option.label}
													</span>
												</div>

												{selected ? (
													<span
														className={classNames(
															active ? 'text-white' : 'text-indigo-600',
															'absolute inset-y-0 right-0 flex items-center pr-4'
														)}
													>
														<CheckIcon className="h-5 w-5" aria-hidden="true" />
													</span>
												) : null}
											</>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</Transition>
					</div>
				</>
			)}
		</Listbox>
	);
}
